<template>
  <section class="nx-products nx-grid-bg" id="products">
    <div class="nx-products-container">
      <div class="nx-products-header nx-animate-on-scroll">
        <p class="nx-products-subtitle">{{ config?.products?.subtitle }}</p>
        <h2 class="nx-products-title">{{ config?.products?.title }}</h2>
        <p class="nx-products-desc">{{ config?.products?.description }}</p>
      </div>

      <!-- 超级智能体横幅 -->
      <div class="nx-hero-banner nx-animate-on-scroll" v-if="config?.products?.heroBanner">
        <div class="nx-hero-content" :style="{ backgroundImage: 'url(' + config.products.heroBanner.image + ')' }">
          <div class="nx-hero-text">
            <h2 class="nx-hero-title">
              {{ config.products.heroBanner.title }}
              <span class="nx-hero-tag">{{ config.products.heroBanner.tag }}</span>
            </h2>
            <div class="nx-hero-desc" v-if="config.products.heroBanner.description">
              <span class="nx-hero-desc-item" v-for="(part, index) in descParts" :key="'desc-' + index">
                {{ part }}
              </span>
            </div>
            <ul class="nx-hero-features" v-if="config.products.heroBanner.features && config.products.heroBanner.features.length">
              <li v-for="(feature, index) in config.products.heroBanner.features" :key="'feature-' + index">
                <span class="nx-feature-icon">✓</span>
                {{ feature }}
              </li>
            </ul>
            <button class="nx-hero-btn" @click="goToSuperAgent">{{ config.products.heroBanner.buttonText }}</button>
          </div>
          <div class="nx-hero-image" v-if="config.products.heroBanner.image">
            <img :src="config.products.heroBanner.image" :alt="config.products.heroBanner.title">
          </div>
        </div>
      </div>

      <!-- TAB 导航 -->
      <div class="nx-tabs-nav nx-animate-on-scroll" v-if="config?.products?.categories">
        <button
          v-for="(category, index) in config.products.categories"
          :key="'tab-' + index"
          class="nx-tab-btn"
          :class="{ 'nx-tab-active': activeTab === index }"
          @click="switchTab(index)"
        >
          <span class="nx-tab-text">{{ category.name }}</span>
        </button>
      </div>

      <!-- TAB 内容 -->
      <div class="nx-tabs-content">
        <div v-if="!config" class="nx-loading">
          <p>加载中...</p>
        </div>
        <div v-else-if="!currentProducts || currentProducts.length === 0" class="nx-empty">
          <p>暂无产品数据</p>
        </div>
        <div v-else class="nx-products-grid">
          <div
            v-for="(product, prodIndex) in currentProducts"
            :key="activeTab + '-' + prodIndex"
            class="nx-product-card nx-card"
            :class="getProductColorClass(prodIndex)"
            :style="getProductColorStyle(prodIndex)"
            @click="handleProductClick(product, activeTab, prodIndex)"
          >
            <div class="nx-tech-corner"></div>
            <div class="nx-product-badge">{{ String(prodIndex + 1).padStart(2, '0') }}</div>
            <h4 class="nx-product-title">{{ product.title }}</h4>
            <p class="nx-product-content">{{ product.content }}</p>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: 'NxProducts',
  props: {
    config: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      activeTab: 0
    };
  },
  computed: {
    currentProducts() {
      if (!this.config?.products?.categories) return [];
      const category = this.config.products.categories[this.activeTab];
      return category?.items || [];
    },
    totalCategories() {
      return this.config?.products?.categories?.length || 0;
    },
    // 将描述按第一个中文逗号拆为两段展示（后续逗号保留在同一段内，保证一句话不拆行），并去掉结尾句号
    descParts() {
      const desc = this.config?.products?.heroBanner?.description || '';
      const idx = desc.indexOf('，');
      if (idx === -1) {
        return [desc.trim().replace(/。$/, '')].filter(Boolean);
      }
      return [
        desc.slice(0, idx),
        desc.slice(idx + 1)
      ]
        .map(part => part.trim().replace(/。$/, ''))
        .filter(Boolean);
    }
  },
  mounted() {
    // 组件已挂载，配置由父组件传递
  },
  beforeUnmount() {
    // 清理工作
  },
  methods: {
    goToSuperAgent() {
      // 在新标签页打开超级智能体详情页
      window.open('/super-agent', '_blank');
    },
    switchTab(index) {
      if (this.activeTab === index) return;
      this.activeTab = index;
    },
    getProductColorClass(index) {
      return `nx-product-color-${index % 12}`;
    },
    getProductColorStyle(index) {
      const colors = {
        primary: '#1976D2',
        secondary: '#42A5F5',
        accent: '#64B5F6',
        glow: 'rgba(25, 118, 210, 0.12)',
        border: 'rgba(25, 118, 210, 0.25)'
      };
      return {
        '--product-primary': colors.primary,
        '--product-secondary': colors.secondary,
        '--product-accent': colors.accent,
        '--product-glow': colors.glow,
        '--product-border': colors.border
      };
    },
    handleProductClick(product, tabIndex, prodIndex) {
      // 获取当前分类名称
      const categoryName = this.config?.products?.categories[tabIndex]?.name || 'product';
      const productIndex = prodIndex + 1;

      // 构建URL参数，包含分类和产品信息
      const params = new URLSearchParams({
        category: categoryName,
        product: product.title,
        index: productIndex
      });

      // 在新标签页打开产品详情页
      const url = `/product-detail?${params.toString()}`;
      window.open(url, '_blank');
    }
  }
};
</script>

<style scoped>
.nx-products {
  position: relative;
  padding: 100px 30px;
  background: linear-gradient(180deg, #fafbfc 0%, #f5f7fa 50%, #f8f9fa 100%);
  overflow: hidden;
}

.nx-products::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 700px;
  background:
    radial-gradient(ellipse at top, rgba(59, 130, 246, 0.06) 0%, transparent 60%),
    radial-gradient(ellipse at 80% 10%, rgba(139, 92, 246, 0.05) 0%, transparent 50%);
  pointer-events: none;
  animation: productTopFloat 12s ease-in-out infinite alternate;
}

@keyframes productTopFloat {
  0% {
    opacity: 0.7;
    transform: translateY(0);
  }
  100% {
    opacity: 1;
    transform: translateY(-30px);
  }
}

.nx-products::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 700px;
  background:
    radial-gradient(ellipse at bottom, rgba(6, 182, 212, 0.05) 0%, transparent 60%),
    radial-gradient(ellipse at 20% 90%, rgba(59, 130, 246, 0.04) 0%, transparent 50%);
  pointer-events: none;
  animation: productBottomFloat 12s ease-in-out infinite alternate-reverse;
}

@keyframes productBottomFloat {
  0% {
    opacity: 0.7;
    transform: translateY(0);
  }
  100% {
    opacity: 1;
    transform: translateY(30px);
  }
}

.nx-products-container {
  max-width: 1400px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
}

.nx-products-header {
  text-align: center;
  margin-bottom: 25px;
}

.nx-products-subtitle {
  font-size: 1.2rem;
  color: var(--nx-blue-primary);
  font-weight: 500;
  letter-spacing: 0.3rem;
  text-transform: uppercase;
  margin-bottom: 0.4rem;
  text-align: center;
  text-shadow: 0 0 20px rgba(25, 118, 210, 0.25);
}

.nx-products-title {
  font-size: clamp(32px, 4.5vw, 48px);
  font-weight: 800;
  color: var(--nx-text-title);
  margin-bottom: 12px;
  text-shadow: 0 0 30px rgba(13, 71, 161, 0.15),
    0 0 60px rgba(59, 130, 246, 0.1);
  position: relative;
  display: inline-block;
}

.nx-products-title::after {
  content: "";
  position: absolute;
  bottom: -8px;
  left: 50%;
  transform: translateX(-50%);
  width: 60px;
  height: 3px;
  background: linear-gradient(
    90deg,
    transparent,
    var(--nx-blue-primary),
    transparent
  );
  border-radius: 2px;
  box-shadow: 0 0 15px rgba(59, 130, 246, 0.6);
  animation: titleGlow 2s ease-in-out infinite;
}

.nx-products-desc {
  max-width: 800px;
  margin: 0 auto;
  font-size: 17px;
  line-height: 1.8;
  color: var(--nx-text-desc);
}

/* 超级智能体横幅 */
.nx-hero-banner {
  margin: 40px 0 50px;
  padding: 0;
  position: relative;
  z-index: 2;
}

.nx-hero-content {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 60px;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  border-radius: 24px;
  padding: 50px 100px 50px 60px;
  box-shadow: 0 20px 60px rgba(37, 99, 235, 0.25),
              0 0 0 1px rgba(255, 255, 255, 0.1) inset;
  position: relative;
  overflow: hidden;
  min-height: 400px;
}

.nx-hero-content::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.2);
  pointer-events: none;
  z-index: 0;
}

.nx-hero-text {
  flex: 0 0 auto;
  max-width: 700px;
  position: relative;
  z-index: 1;
  text-align: left;
}

.nx-hero-image {
  display: none;
}

.nx-hero-tag {
  position: absolute;
  top: -6px;
  right: 60px;
  display: inline-flex;
  align-items: center;
  background: linear-gradient(135deg, rgba(0, 212, 255, 0.15), rgba(0, 128, 255, 0.15));
  color: #00d4ff;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 0.7rem;
  font-weight: 600;
  border: 1px solid rgba(0, 212, 255, 0.4);
  letter-spacing: 0.1em;
  text-transform: uppercase;
  white-space: nowrap;
  z-index: 10;
  -webkit-text-fill-color: #00d4ff;
  backdrop-filter: blur(8px);
  box-shadow: 0 2px 10px rgba(0, 212, 255, 0.15),
              inset 0 0 15px rgba(0, 212, 255, 0.05);
}

.nx-hero-tag::before {
  content: 'NEW';
  color: #00d4ff;
  -webkit-text-fill-color: #00d4ff;
}

.nx-hero-tag {
  font-size: 0;
}

.nx-hero-tag::before {
  font-size: 0.7rem;
}

.nx-hero-tag::after {
  display: none;
}

.nx-hero-title {
  font-size: clamp(24px, 3.2vw, 42px);
  font-weight: 900;
  margin-bottom: 24px;
  position: relative;
  display: inline-block;
  letter-spacing: 0.05em;
  padding-right: 100px;

  /* 渐变文字效果 - 中等强度青色 */
  background: linear-gradient(
    135deg,
    #f5f5f5 0%,
    #81d4fa 25%,
    #f5f5f5 50%,
    #81d4fa 75%,
    #f5f5f5 100%
  );
  background-size: 200% auto;
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  animation: textGradient 4s linear infinite;

  /* 中等强度发光效果 */
  filter: drop-shadow(0 0 12px rgba(0, 212, 255, 0.5))
          drop-shadow(0 0 25px rgba(0, 212, 255, 0.35));
}

@keyframes textGradient {
  0% {
    background-position: 0% center;
  }
  100% {
    background-position: 200% center;
  }
}

.nx-hero-desc {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
  margin: 0 0 25px;
  max-width: 700px;
}

.nx-hero-desc-item {
  font-size: 0.95rem;
  line-height: 1.7;
  color: #1a1a1a;
  white-space: nowrap;
  background: rgba(255, 255, 255, 0.6);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  padding: 8px 16px;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.45);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  text-shadow: 0 1px 2px rgba(255, 255, 255, 0.8);
}

.nx-hero-features {
  list-style: none;
  padding: 0;
  margin: 0 0 30px;
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  justify-content: space-between;
  max-width: 700px;
}

.nx-hero-features li {
  display: flex;
  align-items: center;
  color: #1a1a1a;
  font-size: 0.9rem;
  font-weight: 700;
  flex: 0 0 auto;
  white-space: nowrap;
  /* 半透明白色背景提高可读性 */
  background: rgba(255, 255, 255, 0.75);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  padding: 6px 10px;
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.4);
  /* 文字阴影增强对比度 */
  text-shadow: 0 1px 2px rgba(255, 255, 255, 0.8);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.nx-feature-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  background: linear-gradient(135deg, #64b5f6 0%, #42a5f5 100%);
  border-radius: 50%;
  margin-right: 8px;
  font-size: 12px;
  font-weight: bold;
  flex-shrink: 0;
  color: #ffffff;
  box-shadow: 0 2px 6px rgba(66, 165, 245, 0.4);
}

.nx-hero-btn {
  background: linear-gradient(135deg, #64b5f6 0%, #42a5f5 100%);
  color: #ffffff;
  border: none;
  padding: 14px 36px;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 800;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 20px rgba(66, 165, 245, 0.4),
              inset 0 1px 0 rgba(255, 255, 255, 0.2);
  position: relative;
  overflow: hidden;
  text-shadow: 0 0 10px rgba(255, 255, 255, 0.5);
  letter-spacing: 0.05em;
  text-transform: uppercase;
  clip-path: polygon(
    0 0,
    calc(100% - 15px) 0,
    100% 15px,
    100% 100%,
    15px 100%,
    0 calc(100% - 15px)
  );

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.4),
      transparent
    );
    transition: left 0.5s ease;
  }

  &::after {
    content: '►';
    position: absolute;
    right: 20px;
    top: 50%;
    transform: translateY(-50%);
    font-size: 0.8em;
    opacity: 0;
    transition: all 0.3s ease;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 30px rgba(66, 165, 245, 0.5),
                inset 0 1px 0 rgba(255, 255, 255, 0.3);
    background: linear-gradient(135deg, #90caf9 0%, #64b5f6 100%);
    padding-right: 45px;

    &::after {
      opacity: 1;
      right: 18px;
    }
  }

  &:active {
    transform: translateY(0);
  }
}

.nx-hero-btn::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent);
  transition: left 0.5s ease;
}

.nx-hero-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 30px rgba(66, 165, 245, 0.5);
}

.nx-hero-btn:hover::before {
  left: 100%;
}

.nx-hero-image {
  flex: 0 0 400px;
  position: relative;
  z-index: 1;
}

.nx-hero-image img {
  width: 100%;
  height: auto;
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  transition: transform 0.3s ease;
}

.nx-hero-image img:hover {
  transform: scale(1.05);
}

/* TAB 导航 - 奢华版 */
.nx-tabs-nav {
  display: flex;
  justify-content: center;
  gap: 3rem;
  margin-bottom: 45px;
  flex-wrap: wrap;
  padding: 0 2rem;
  position: relative;
}

.nx-tabs-nav::before {
  content: '';
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 80%;
  height: 1px;
  background: linear-gradient(90deg,
    transparent 0%,
    rgba(25, 118, 210, 0.3) 50%,
    transparent 100%);
}

.nx-tab-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0.8rem 1.2rem;
  background: transparent;
  border: none;
  cursor: pointer;
  transition: all 0.5s cubic-bezier(0.23, 1, 0.32, 1);
  font-size: 1.1rem;
  font-weight: 500;
  color: var(--nx-text-gray);
  position: relative;
  outline: none;
  border-radius: 12px;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(circle at center, rgba(25, 118, 210, 0.1) 0%, transparent 70%);
    opacity: 0;
    transition: opacity 0.4s ease;
    border-radius: inherit;
  }

  &::after {
    content: '';
    position: absolute;
    bottom: -0.5rem;
    left: 50%;
    transform: translateX(-50%);
    width: 0;
    height: 3px;
    background: var(--nx-gradient-blue);
    background-size: 200% 200%;
    transition: all 0.5s cubic-bezier(0.23, 1, 0.32, 1);
    box-shadow: 0 0 15px rgba(25, 118, 210, 0.6);
    border-radius: 2px;
  }

  &:hover {
    color: var(--nx-blue-primary);
    text-shadow: 0 0 25px rgba(25, 118, 210, 0.5);
    transform: translateY(-2px);

    &::before {
      opacity: 1;
    }

    &::after {
      width: 60%;
    }
  }

  .nx-tab-icon,
  .nx-tab-text {
    position: relative;
    z-index: 1;
  }
}

.nx-tab-btn.nx-tab-active {
  color: var(--nx-blue-primary);
  text-shadow: 0 0 30px rgba(25, 118, 210, 0.6);
  transform: translateY(-2px);

  &::before {
    content: '';
    position: absolute;
    inset: -4px;
    background: radial-gradient(ellipse at center, rgba(25, 118, 210, 0.15) 0%, transparent 70%);
    opacity: 1;
    animation: tabGlow 2s ease-in-out infinite alternate;
    border-radius: 16px;
  }

  &::after {
    width: 100%;
    animation: blueShine 4s linear infinite, tabLinePulse 2s ease-in-out infinite;
  }

  .nx-tab-icon {
    animation: iconRotate 0.6s cubic-bezier(0.23, 1, 0.32, 1);
    filter: drop-shadow(0 0 8px rgba(25, 118, 210, 0.6));
  }

  .nx-tab-text {
    animation: textScale 0.4s cubic-bezier(0.23, 1, 0.32, 1);
  }
}

@keyframes tabGlow {
  0% {
    opacity: 0.6;
    transform: scale(1);
  }
  100% {
    opacity: 1;
    transform: scale(1.05);
  }
}

@keyframes tabLinePulse {
  0%, 100% {
    box-shadow: 0 0 15px rgba(25, 118, 210, 0.6);
  }
  50% {
    box-shadow: 0 0 25px rgba(25, 118, 210, 0.9), 0 0 35px rgba(66, 165, 245, 0.4);
  }
}

@keyframes iconRotate {
  0% {
    transform: rotate(-5deg) scale(0.95);
  }
  50% {
    transform: rotate(3deg) scale(1.05);
  }
  100% {
    transform: rotate(0) scale(1);
  }
}

@keyframes textScale {
  0% {
    transform: scale(0.95);
    opacity: 0.8;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

.nx-tab-icon {
  font-size: 20px;
  line-height: 1;
}

.nx-tab-text {
  font-size: 1.1rem;
  font-weight: 500;
}

/* TAB 内容 */
.nx-tabs-content {
  min-height: 400px;
  position: relative;
}

.nx-products-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  justify-content: center;
  max-width: 1200px;
  margin: 0 auto;
}

.nx-product-card {
  animation: cardFadeIn 0.3s ease-out backwards;
}

/* 为每个卡片添加延迟动画 */
.nx-products-grid .nx-product-card:nth-child(1) { animation-delay: 0.05s; }
.nx-products-grid .nx-product-card:nth-child(2) { animation-delay: 0.1s; }
.nx-products-grid .nx-product-card:nth-child(3) { animation-delay: 0.15s; }
.nx-products-grid .nx-product-card:nth-child(4) { animation-delay: 0.2s; }
.nx-products-grid .nx-product-card:nth-child(5) { animation-delay: 0.25s; }

@keyframes cardFadeIn {
  0% {
    opacity: 0;
    transform: translateY(10px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

.nx-product-card {
  padding: 20px 18px;
  background: linear-gradient(145deg,
    rgba(255, 255, 255, 0.98) 0%,
    rgba(248, 250, 255, 0.95) 100%);
  backdrop-filter: blur(30px);
  border: 2px solid var(--product-border, rgba(21, 101, 192, 0.15));
  border-radius: var(--nx-radius-md);
  transition: all 0.6s cubic-bezier(0.23, 1, 0.32, 1);
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-height: 200px;
  box-shadow:
    0 8px 30px var(--product-glow, rgba(21, 101, 192, 0.12)),
    0 0 40px var(--product-glow, rgba(21, 101, 192, 0.06));
  cursor: pointer;

  /* 顶部渐变条 */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 4px;
    background: linear-gradient(90deg,
      var(--product-primary) 0%,
      var(--product-secondary) 25%,
      var(--product-accent) 50%,
      var(--product-secondary) 75%,
      var(--product-primary) 100%);
    background-size: 200% 100%;
    transform: scaleX(0);
    transform-origin: left;
    transition: transform 0.6s cubic-bezier(0.23, 1, 0.32, 1);
    box-shadow:
      0 0 25px var(--product-glow),
      0 4px 15px var(--product-glow);
    animation: cardTopShine 4s linear infinite;
  }

  @keyframes cardTopShine {
    0% {
      background-position: -200% center;
    }
    100% {
      background-position: 200% center;
    }
  }

  /* 右下角科技感装饰 */
  .nx-tech-corner {
    position: absolute;
    bottom: 0;
    right: 0;
    width: 40px;
    height: 40px;
    pointer-events: none;
    opacity: 0.4;

    &::before {
      content: '';
      position: absolute;
      bottom: 8px;
      right: 8px;
      width: 24px;
      height: 24px;
      border-right: 2px solid var(--product-primary);
      border-bottom: 2px solid var(--product-primary);
    }

    &::after {
      content: '';
      position: absolute;
      bottom: 14px;
      right: 14px;
      width: 12px;
      height: 12px;
      border-right: 2px solid var(--product-accent);
      border-bottom: 2px solid var(--product-accent);
    }
  }

  /* 内部内容提高层级 */
  > *:not(.nx-tech-corner) {
    position: relative;
    z-index: 2;
  }

  &:hover {
    transform: translateY(-10px) scale(1.02);
    border-color: var(--product-border);
    box-shadow:
      0 20px 60px var(--product-glow),
      0 0 80px var(--product-glow);

    &::before {
      transform: scaleX(1);
    }
  }
}

.nx-product-badge {
  width: 46px;
  height: 46px;
  background: linear-gradient(135deg,
    var(--product-primary) 0%,
    var(--product-secondary) 50%,
    var(--product-accent) 100%);
  background-size: 200% 200%;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: 800;
  color: #ffffff;
  margin-bottom: 14px;
  box-shadow:
    0 6px 20px var(--product-glow),
    0 0 30px var(--product-glow),
    inset 0 1px 0 rgba(255, 255, 255, 0.25);
  transition: all 0.5s cubic-bezier(0.23, 1, 0.32, 1);
  position: relative;
  overflow: hidden;
}

.nx-product-badge::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.35),
    transparent
  );
  transition: left 0.6s;
}

.nx-product-card:hover .nx-product-badge {
  transform: scale(1.15) rotate(5deg);
  box-shadow:
    0 10px 35px var(--product-glow),
    0 0 50px var(--product-glow),
    inset 0 1px 0 rgba(255, 255, 255, 0.3);
}

.nx-product-card:hover .nx-product-badge::before {
  left: 100%;
}

.nx-product-title {
  font-size: 18px;
  font-weight: 800;
  color: var(--product-primary);
  margin-bottom: 10px;
  line-height: 1.3;
  transition: all 0.3s ease;
}

.nx-product-card:hover .nx-product-title {
  text-shadow: 0 0 20px var(--product-glow);
}

.nx-product-content {
  font-size: 14px;
  line-height: 1.6;
  color: var(--nx-text-desc);
  flex-grow: 1;
  transition: color 0.3s ease;
}

.nx-product-card:hover .nx-product-content {
  color: var(--nx-text-white);
}

/* 加载和空状态 */
.nx-loading,
.nx-empty {
  text-align: center;
  padding: 80px 20px;
  color: var(--nx-text-dark);
  font-size: 16px;
  min-height: 300px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.nx-loading p,
.nx-empty p {
  margin: 0;
}

@media (max-width: 1200px) {
  .nx-products-grid {
    grid-template-columns: repeat(3, 1fr);
    gap: 18px;
    max-width: 900px;
  }

  .nx-hero-content {
    gap: 40px;
    padding: 45px 50px;
    min-height: 420px;
  }
}

@media (max-width: 900px) {
  .nx-products-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
    max-width: 600px;
  }

  .nx-hero-content {
    gap: 30px;
    padding: 40px 35px;
    min-height: 380px;
  }

  .nx-hero-text {
    max-width: 100%;
  }
}

@media (max-width: 768px) {
  .nx-products {
    padding: 70px 20px;
  }

  .nx-products-header {
    margin-bottom: 35px;
  }

  .nx-tabs-nav {
    gap: 1.5rem;
    margin-bottom: 35px;
    padding: 0 1rem;
  }

  .nx-hero-banner {
    margin: 30px 0 40px;
  }

  .nx-hero-content {
    padding: 35px 30px;
    min-height: 180px;
    justify-content: center;
    align-items: center;
  }

  .nx-hero-text {
    max-width: 100%;
    text-align: center;
    margin-left: 70px;
    margin-top: 30px;
  }

  .nx-hero-title {
    font-size: clamp(18px, 4.5vw, 28px);
    margin-bottom: 16px;
    padding-right: 0;
    display: block;
  }

  /* 移动端隐藏特征列表 */
  .nx-hero-features {
    display: none;
  }

  .nx-hero-desc {
    display: none;
  }

  .nx-hero-btn {
    padding: 10px 24px;
    font-size: 0.75rem;
    letter-spacing: 0.05em;
    display: inline-block;
  }

  .nx-hero-tag {
    top: -8px;
    right: -40px;
    padding: 5px 10px;
    position: absolute;
  }

  .nx-hero-tag::before {
    font-size: 0.6rem;
  }

  .nx-tab-btn {
    padding: 0.5rem 0;
    font-size: 1rem;
  }

  .nx-tab-icon {
    font-size: 18px;
  }

  .nx-tab-text {
    font-size: 1rem;
  }

  .nx-products-grid {
    grid-template-columns: 1fr;
    gap: 14px;
    max-width: 400px;
  }

  .nx-product-card {
    padding: 24px 18px;
    min-height: 220px;
  }

  .nx-product-badge {
    width: 48px;
    height: 48px;
    font-size: 18px;
    margin-bottom: 16px;
    background: linear-gradient(135deg,
      var(--product-primary) 0%,
      var(--product-secondary) 50%,
      var(--product-accent) 100%);
    background-size: 200% 200%;
  }

  .nx-product-title {
    font-size: 16px;
    margin-bottom: 12px;
    color: var(--product-primary);
  }

  .nx-product-content {
    font-size: 13px;
    line-height: 1.6;
  }

  .nx-products-subtitle {
    font-size: 1rem;
    letter-spacing: 0.2rem;
  }
}

/* 标题光效动画 */
@keyframes titleGlow {
  0%, 100% {
    width: 60px;
    opacity: 0.6;
  }
  50% {
    width: 120px;
    opacity: 1;
  }
}
</style>
